import { graphql } from "gatsby";
import * as React from "react";
import ReactDOM from "react-dom";

import dayjs from "dayjs";
import { ImageDataLike } from "gatsby-plugin-image";
import {
    ContentfulRichTextGatsbyReference,
    renderRichText,
    RenderRichTextData
} from "gatsby-source-contentful/rich-text";
import { useRef } from "react";
import { BlogPageHero } from "../components/blog/hero";
import { BlogLinks } from "../components/blog/links";
import { Layout } from "../components/layout/layout";
import { OverviewItem } from "../components/shared/overview-item";
import { PageIntro } from "../components/shared/page-intro";
import { useContentful } from "../hooks/useContentful";
import { slugify } from "../services/slugify";

interface Query {
    contentfulPost: {
        title: string;
        slug: string;
        updatedAt: string;
        content: RenderRichTextData<ContentfulRichTextGatsbyReference>;
        introContent: RenderRichTextData<ContentfulRichTextGatsbyReference>;
        blogCategory: {
            name: string;
            slug: string;
            groupId: string;
        }[];
        description: {
            description: string;
        };
        month: {
            month: string;
        };
        image: {
            localFile: { childImageSharp: { gatsbyImageData: ImageDataLike } };
        };
        imageHero: {
            localFile: { childImageSharp: { gatsbyImageData: ImageDataLike } };
        };
    };
}

const BlogPost = ({ data }: { data: Query }) => {
    const { title, slug, image, imageHero, description, blogCategory } =
        data.contentfulPost;
    const links: { path: string; text: string }[] = [];
    links.push({ path: "/blog", text: "Blog" });
    let continent: string = null;
    for (let category of data.contentfulPost.blogCategory) {
        links.push({ path: `/blog/${category.slug}`, text: category.name });
        if (category.groupId === "continent") {
            continent = category.slug;
        }
    }
    return (
        <Layout
            title={title}
            description={description.description}
            path={"/blog/" + slug + "/"}
        >
            <BlogPageHero
                className="lg:hidden"
                bgImage={image.localFile.childImageSharp.gatsbyImageData}
                header={title}
                links={links}
            ></BlogPageHero>
            <BlogPageHero
                className="lgmax:hidden"
                bgImage={imageHero.localFile.childImageSharp.gatsbyImageData}
                header={title}
                links={links}
            ></BlogPageHero>
            <BlogPostInternal data={data} />
            <BlogLinks continent={continent} excludeSlug={slug} />
        </Layout>
    );
};

const BlogPostInternal = ({ data }: { data: Query }) => {
    const { content, title, slug, month, introContent } = data.contentfulPost;
    const pageOverviewRef = useRef<HTMLUListElement>();
    const headingsRef = useRef<string[]>();
    const { getOptions } = useContentful();
    React.useEffect(() => {
        if (!pageOverviewRef.current) {
            return;
        }
        ReactDOM.render(
            <>
                {headingsRef.current.map((heading) => (
                    <OverviewItem
                        key={heading}
                        name={heading}
                        id={`#${slugify(heading)}`}
                    />
                ))}
            </>,
            pageOverviewRef.current
        );
    }, [headingsRef, pageOverviewRef]);
    const updatedAt = dayjs(data.contentfulPost.updatedAt).format("YYYY-MM-DD");
    return (
        <div className="mt-12" data-updated={updatedAt}>
            <div className="w-page-overview m-auto mb-6 mt-3">
                <PageIntro
                    overviewNode={<ul ref={pageOverviewRef} />}
                    introContent={
                        introContent &&
                        renderRichText(
                            introContent,
                            getOptions(pageOverviewRef, headingsRef)
                        )
                    }
                />
            </div>
            <div className="w-text-article m-auto">
                {content &&
                    renderRichText(
                        content,
                        getOptions(pageOverviewRef, headingsRef)
                    )}
            </div>
        </div>
    );
};

export default BlogPost;

export const query = graphql`
    query pageQuery($id: String!) {
        contentfulPost(id: { eq: $id }) {
            slug
            title
            updatedAt
            blogCategory {
                groupId
                name
                slug
            }
            description {
                description
            }
            month {
                month
            }
            image {
                localFile {
                    childImageSharp {
                        gatsbyImageData(layout: FULL_WIDTH)
                    }
                }
            }
            imageHero {
                localFile {
                    childImageSharp {
                        gatsbyImageData(layout: FULL_WIDTH, breakpoints: [1920])
                    }
                }
            }
            introContent {
                raw
                references {
                    ... on ContentfulLinkToRegRes {
                        contentful_id
                        __typename
                        text
                        region {
                            slug
                        }
                        resort {
                            slug
                        }
                    }
                }
            }
            content {
                raw
                references {
                    ... on ContentfulAsset {
                        contentful_id
                        __typename
                        description
                        localFile {
                            childImageSharp {
                                gatsbyImageData(width: 660)
                            }
                        }
                    }
                    ... on ContentfulPageOverview {
                        contentful_id
                        __typename
                    }
                    ... on ContentfulLinkToRegRes {
                        contentful_id
                        __typename
                        text
                        region {
                            slug
                        }
                        resort {
                            slug
                        }
                    }
                    ... on ContentfulMapSearch {
                        contentful_id
                        __typename
                        continent {
                            slug
                        }
                    }
                }
            }
        }
    }
`;
